import { useEffect, useState } from 'react';
import { Card, Heading, Button, Pane, majorScale, toaster, Checkbox } from 'evergreen-ui';
import { EditIcon, RemoveIcon, DocumentIcon } from 'evergreen-ui';
import { removeAnnouncement, markAnnouncementAsRead } from '../services/announcementServices';
import { getUserDetailsByIds } from '../services/userServices';
import '../styles/responsive.css';

const AnnouncementList = ({ announcements, announcementViews, handleSelectAnnouncement, setAnnouncements, userId, userRole }) => {
    const [readAnnouncements, setReadAnnouncements] = useState(new Set(announcementViews.map(view => view.announcementId)));
    const [userDetails, setUserDetails] = useState({});
    const [hideRead, setHideRead] = useState(false); 
    const [sortField, setSortField] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('desc'); 
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

    useEffect(() => {
        const fetchUserDetails = async () => {
            const userIds = announcementViews
                .filter(view => view && view.user_id !== undefined)
                .map(view => view.user_id);

            if (userIds.length === 0) {
                console.warn('Nessun ID utente valido trovato in announcementViews:', announcementViews);
                return;
            }

            const uniqueUserIds = [...new Set(userIds)];

            try {
                const userDetailsResponse = await getUserDetailsByIds(uniqueUserIds);
                const userDetailsMap = userDetailsResponse.reduce((acc, user) => {
                    acc[user.id] = user;
                    return acc;
                }, {});

                setUserDetails(userDetailsMap);
            } catch (error) {
                console.error('Errore nel recupero dei dettagli degli utenti:', error);
            }
        };

        if (Array.isArray(announcementViews) && announcementViews.length > 0) {
            fetchUserDetails();
        }
    }, [announcementViews]);

    const handleRemoveAnnouncement = async (index) => {
        try {
            const announcementToRemove = announcements[index];
            await removeAnnouncement(announcementToRemove.id);
            setAnnouncements((prev) => prev.filter((_, i) => i !== index));
            toaster.notify('Annuncio rimosso con successo.');
        } catch (error) {
            console.error('Errore nella rimozione dell\'annuncio:', error);
            toaster.danger('Errore nella rimozione dell\'annuncio.');
        }
    };

    const handleMarkAsRead = async (id) => {
        if (readAnnouncements.has(id)) return; 
        try {
            await markAnnouncementAsRead(id, userId);
            setReadAnnouncements((prev) => new Set(prev).add(id));
            toaster.success('Annuncio segnato come letto.');
        } catch (error) {
            console.error('Errore nel segnare come letto:', error);
            toaster.danger('Errore nel segnare come letto.');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    useEffect(() => {
        const updatedReadAnnouncements = new Set(
            announcementViews
                .filter((view) => view.user_id === userId)
                .map((view) => view.announcement_id)
        );
        setReadAnnouncements(updatedReadAnnouncements);
    }, [announcementViews, userId]);

    const filteredAnnouncements = hideRead
        ? announcements.filter((announcement) => !readAnnouncements.has(announcement.id))
        : announcements; 

    const sortedAnnouncements = [...filteredAnnouncements].sort((a, b) => {
        const dateA = new Date(sortOrder === 'asc' ? a[sortField] : b[sortField]);
        const dateB = new Date(sortOrder === 'asc' ? b[sortField] : a[sortField]);
        return dateA - dateB;
    });

    const handleSelectAnnouncementToggle = (announcement) => {
        setSelectedAnnouncement((prev) => (prev?.id === announcement.id ? null : announcement));
    };

    return (
        <Pane>
            <Heading size={600} marginBottom={16}>
                Annunci pubblicati
            </Heading>
            {(userRole === 'operatore' || userRole === 'educatore') && (
                <Pane marginBottom={majorScale(2)}>
                    <Checkbox
                        label="Nascondi già letti"
                        checked={hideRead}
                        onChange={(e) => setHideRead(e.target.checked)}
                    />
                </Pane>
            )}
            <Pane marginBottom={majorScale(2)} className="responsive-button-group" display="flex" gap={16} alignItems="center">
                <Button
                    appearance="primary"
                    intent="success"
                    onClick={() => {
                        setSortField('timestamp');
                        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                    }}
                >
                    Ordina per data di creazione {sortField === 'timestamp' && (sortOrder === 'asc' ? '↑' : '↓')}
                </Button>
                <Button
                    appearance="primary"
                    intent="success"
                    onClick={() => {
                        setSortField('last_modified');
                        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                    }}
                >
                    Ordina per data di modifica {sortField === 'last_modified' && (sortOrder === 'asc' ? '↑' : '↓')}
                </Button>
            </Pane>
            {Array.isArray(sortedAnnouncements) && sortedAnnouncements.map((announcement, index) => (
                <Card key={index} className="card-flex" elevation={1} padding={16} marginBottom={8}>
                    <Pane className="card-header">
                        <Heading size={500} onClick={() => handleSelectAnnouncementToggle(announcement)} style={{ cursor: 'pointer' }}>
                            {announcement.title}
                        </Heading>
                        <Pane>
                            <div className="date-line">Creato: {formatDate(announcement.timestamp)}</div>
                            {announcement.last_modified && (
                                <div className="date-line">Modificato: {formatDate(announcement.last_modified)}</div>
                            )}
                        </Pane>
                    </Pane>
                    {selectedAnnouncement?.id === announcement.id && (
                        <>
                            <Pane className="card-body" marginTop={16}>
                                <p>{announcement.body}</p>
                                {Array.isArray(announcement.attachments) && announcement.attachments.filter(att => att && att.url && att.name).length > 0 && (
                                    <Pane marginTop={16}>
                                        <Heading size={400} marginBottom={8}>Allegati:</Heading>
                                        <ul style={{ paddingLeft: 16 }}>
                                            {announcement.attachments.filter(att => att && att.url && att.name).map((attachment, idx) => (
                                                <li key={idx}>
                                                    <a href={attachment.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                        <DocumentIcon flexShrink={0} /> {attachment.name}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </Pane>
                                )}
                                {userRole === 'direttore' && (
                                    <Pane marginTop={majorScale(2)}>
                                        <Heading size={400}>Visualizzato da:</Heading>
                                        <ul>
                                            {announcementViews && announcementViews.length > 0 ? (
                                                announcementViews
                                                    .filter((view) => view.announcement_id === announcement.id)
                                                    .map((view) => (
                                                        <li key={`${view.announcement_id}-${view.user_id}`}>
                                                            {userDetails[view.user_id]?.nome || 'Nome non disponibile'} {userDetails[view.user_id]?.cognome || ''}
                                                            {view.viewed_at ? ` - ${formatDate(view.viewed_at)}` : ''}
                                                        </li>
                                                    ))
                                            ) : (
                                                <li>Nessuno ha ancora visualizzato questo annuncio.</li>
                                            )}
                                        </ul>
                                    </Pane>
                                )}
                                {(userRole === 'operatore' || userRole === 'educatore') && (
                                    <Pane marginTop={majorScale(2)}>
                                        <Checkbox
                                            label={readAnnouncements.has(announcement.id) ? "Letto" : "Segna come letto"}
                                            checked={readAnnouncements.has(announcement.id)}
                                            disabled={readAnnouncements.has(announcement.id)}
                                            onChange={() => handleMarkAsRead(announcement.id)}
                                        />
                                    </Pane>
                                )}
                            </Pane>
                            {userRole === 'direttore' && (
                                <Pane className="card-actions" display="flex" gap={8} marginTop={majorScale(2)}>
                                    <Button appearance="primary" intent="success" onClick={() => handleSelectAnnouncement(index)}>
                                        Modifica <EditIcon marginLeft={8} />
                                    </Button>
                                    <Button intent="danger" onClick={() => handleRemoveAnnouncement(index)}>
                                        Rimuovi <RemoveIcon marginLeft={8} />
                                    </Button>
                                </Pane>
                            )}
                        </>
                    )}
                    {typeof announcement.attachments === 'string' && (
                        announcement.attachments = JSON.parse(announcement.attachments)
                    )}
                </Card>
            ))}
        </Pane>
    );
};

export default AnnouncementList;